import apiClient from "./apiClient";

const FIN = "/api/Financial";
const SP = "/api/SupplierPayment";

const toDate = (d) => (d ? `${d}T00:00:00` : null);
const toNum = (v) => Number(v) || 0;

// Expenses
export const getExpenses = (params) => apiClient.get(`${FIN}/expense/all`, { params });
export const createExpense = (data) =>
  apiClient.post(`${FIN}/expense`, {
    expenseDate: toDate(data.expenseDate),
    accountHeadID: toNum(data.accountHeadId || data.accountHeadID),
    amount: toNum(data.amount),
    paymentType: data.paymentMode || data.paymentType || "Cash",
    remarks: [data.description, data.remarks].filter(Boolean).join(" — ") || "",
  });
export const updateExpense = (id, data) =>
  apiClient.put(`${FIN}/expense/${id}`, {
    expenseDate: toDate(data.expenseDate),
    accountHeadID: toNum(data.accountHeadId || data.accountHeadID),
    amount: toNum(data.amount),
    paymentType: data.paymentMode || data.paymentType || "Cash",
    remarks: [data.description, data.remarks].filter(Boolean).join(" — ") || "",
  });
export const deleteExpense = (id) => apiClient.delete(`${FIN}/expense/${id}`);

// Additional Income
export const getIncomeEntries = (params) => apiClient.get(`${FIN}/income/all`, { params });
export const createIncomeEntry = (data) =>
  apiClient.post(`${FIN}/income`, {
    incomeDate: toDate(data.incomeDate),
    accountHeadID: toNum(data.accountHeadId || data.accountHeadID),
    amount: toNum(data.amount),
    paymentType: data.paymentMode || data.paymentType || "Cash",
    remarks: [data.description, data.remarks].filter(Boolean).join(" — ") || "",
  });
export const updateIncomeEntry = (id, data) =>
  apiClient.put(`${FIN}/income/${id}`, {
    incomeDate: toDate(data.incomeDate),
    accountHeadID: toNum(data.accountHeadId || data.accountHeadID),
    amount: toNum(data.amount),
    paymentType: data.paymentMode || data.paymentType || "Cash",
    remarks: [data.description, data.remarks].filter(Boolean).join(" — ") || "",
  });
export const deleteIncomeEntry = (id) => apiClient.delete(`${FIN}/income/${id}`);

// Journal Entries
export const getJournalEntries = (params) => apiClient.get(`${FIN}/journal/all`, { params });
export const createJournalEntry = (data) => {
  const lines = data.lines || [];
  const debitLine = lines.find((l) => toNum(l.debitAmount) > 0) || {};
  const creditLine = lines.find((l) => toNum(l.creditAmount) > 0) || {};
  return apiClient.post(`${FIN}/journal`, {
    journalDate: toDate(data.journalDate),
    debitAccountHeadID: toNum(debitLine.accountHeadId || debitLine.accountHeadID),
    debitAmount: toNum(debitLine.debitAmount),
    creditAccountHeadID: toNum(creditLine.accountHeadId || creditLine.accountHeadID),
    creditAmount: toNum(creditLine.creditAmount),
    remarks: data.narration || data.remarks || "",
  });
};
export const updateJournalEntry = (id, data) => {
  const lines = data.lines || [];
  const debitLine = lines.find((l) => toNum(l.debitAmount) > 0) || {};
  const creditLine = lines.find((l) => toNum(l.creditAmount) > 0) || {};
  return apiClient.put(`${FIN}/journal/${id}`, {
    journalID: id,
    journalDate: toDate(data.journalDate),
    debitAccountHeadID: toNum(debitLine.accountHeadId || debitLine.accountHeadID),
    debitAmount: toNum(debitLine.debitAmount),
    creditAccountHeadID: toNum(creditLine.accountHeadId || creditLine.accountHeadID),
    creditAmount: toNum(creditLine.creditAmount),
    remarks: data.narration || data.remarks || "",
  });
};
export const deleteJournalEntry = (id) => apiClient.delete(`${FIN}/journal/${id}`);

// Supplier Advance
export const getSupplierAdvances = (params) => apiClient.get(`${SP}/advances/all`, { params });
export const createSupplierAdvance = (data) =>
  apiClient.post(`${SP}/advance`, {
    supplierID: toNum(data.supplierId || data.supplierID),
    entryDate: toDate(data.advanceDate || data.entryDate),
    amount: toNum(data.amount),
    paymentType: data.paymentMode || data.paymentType || "Cash",
    remarks: data.remarks || "",
  });
export const updateSupplierAdvance = (id, data) =>
  apiClient.put(`${SP}/advance/${id}`, {
    advanceID: id,
    supplierID: toNum(data.supplierId || data.supplierID),
    entryDate: toDate(data.advanceDate || data.entryDate),
    amount: toNum(data.amount),
    paymentType: data.paymentMode || data.paymentType || "Cash",
    remarks: data.remarks || "",
  });
export const deleteSupplierAdvance = (id) => apiClient.delete(`${SP}/advance/${id}`);

// Supplier Bill-wise Payments
export const getSupplierPayments = (params) => apiClient.get(`${SP}/billwise/all`, { params });
export const createSupplierPayment = (data) =>
  apiClient.post(`${SP}/billwise`, {
    supplierID: toNum(data.supplierId || data.supplierID),
    paymentDate: toDate(data.paymentDate),
    paymentType: data.paymentMode || data.paymentType || "Cash",
    totalPaidAmount: toNum(data.totalAmount),
    remarks: data.remarks || "",
    details: (data.billAllocations || []).map((b) => ({
      purchaseID: toNum(b.purchaseId || b.purchaseID),
      payingAmount: toNum(b.amount),
      discountAmount: 0,
      balanceAmount: 0,
    })),
  });
export const updateSupplierPayment = (id, data) =>
  apiClient.put(`${SP}/billwise/${id}`, {
    billWisePaymentID: id,
    supplierID: toNum(data.supplierId || data.supplierID),
    paymentDate: toDate(data.paymentDate),
    paymentType: data.paymentMode || data.paymentType || "Cash",
    totalPaidAmount: toNum(data.totalAmount || data.totalPaidAmount),
    remarks: data.remarks || "",
  });

// Pending bills for a supplier (used in SupplierPaymentModal)
export const getSupplierPendingBills = (supplierId) =>
  apiClient.get(`${SP}/pending-bills/${supplierId}`);

// Auto-fill bill allocations by amount
export const getSupplierPaymentAutofill = (supplierId, amount) =>
  apiClient.get(`${SP}/autofill/${supplierId}/${amount}`);
